import DateTimePickerModal from "@/src/components/action-modals/date-time-modal";
import ScButton from "@/src/components/ui-kit/button";
import InputField from "@/src/components/ui-kit/input-field.rhf";
import { AttachmentItem, EventType, Step1FormValues } from "@/src/models/event.model";
import { useTheme } from "@/src/styles/theme-provider";
import { pickFile, pickMedia } from "@/src/utils/picker-utils";
import {
  formatDisplayDate,
  formatDisplayTime,
  toHHmm,
} from "@/src/utils/string.utils";
import {
  AttachCircle,
  CalendarEdit,
  CloseCircle,
  Image,
} from "iconsax-react-nativejs";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Pressable,
  Image as RNImage,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Toast from "react-native-toast-message";
import { FileAttachment } from "../../posts/post/file-attachment";
import CategorySelector from "../../ui-kit/category-selector/category-selector";
import ScToggle from "../../ui-kit/toggle";

type Step1DetailsProps = Readonly<{
  defaultValues: Step1FormValues | null;
  existingCoverUrl?: string | null;
  existingAttachments?: string[];
  onSubmit: (data: Step1FormValues) => void;
  onRemoveExistingCover?: () => void;
  onCancel: () => void;
}>;

const Step1Details = ({
  defaultValues,
  existingCoverUrl,
  existingAttachments = [],
  onRemoveExistingCover,
  onSubmit,
  onCancel,
}: Step1DetailsProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [remainingExistingAttachments, setRemainingExistingAttachments] =
    useState<string[]>(existingAttachments);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Step1FormValues>({
    defaultValues: {
      title: "",
      description: "",
      singleDay: false,
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      locationName: "",
      // The location is typed by hand (no map picker), so there are no
      // coordinates to record. The Data Gateway declares both as non-null
      // Floats, so they are sent as 0 rather than omitted.
      locationLat: 0,
      locationLng: 0,
      categoryIds: [],
      // CR-B2-10: defaults to InPerson; schema-blocked until Data Gateway sync (open item 5)
      eventType: EventType.IN_PERSON,
      meetingLink: "",
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    setRemainingExistingAttachments(existingAttachments);
  }, [existingAttachments]);

  const singleDay = watch("singleDay");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const coverImage = watch("coverImage");
  const attachments = watch("attachments") ?? [];
  const eventType = watch("eventType") ?? EventType.IN_PERSON;
  const isOnlineEvent = eventType === EventType.ONLINE;

  const handlePickImage = async () => {
    const images = await pickMedia(false, "images");
    if (images) {
      setValue("coverImage", images[0]);
    } else {
      Toast.show({ type: "error", text1: t("ERROR_NO_IMAGES_SELECTED") });
    }
  };
  const handlePickAttachments = async () => {
    const files = await pickFile(true);
    if (files && !files.canceled) {
      setValue("attachments", [...attachments, ...files.assets]);
    }
  };

  const handleRemoveNewAttachment = (index: number) => {
    setValue(
      "attachments",
      attachments.filter((_, attachmentIndex) => attachmentIndex !== index),
    );
  };

  const handleRemoveExistingAttachment = (url: string) => {
    setRemainingExistingAttachments((prev) =>
      prev.filter((existingUrl) => existingUrl !== url),
    );
  };

  const handleSubmitWithExisting = (data: Step1FormValues) => {
    onSubmit({ ...data, existingAttachments: remainingExistingAttachments });
  };

  const combinedAttachments: AttachmentItem[] = [
    ...remainingExistingAttachments.map((url) => ({
      type: "existing" as const,
      url,
    })),
    ...attachments.map((file) => ({
      type: "new" as const,
      file,
    })),
  ];

  return (
    <View>
      <InputField
        showLabel={true}
        name="title"
        label={t("LABEL_EVENT_TITLE")}
        control={control}
        rules={{
          required: { value: true, message: t("ERROR_TITLE_REQUIRED") },
        }}
        keyboardType="default"
        backgroundColor="bg-chalk"
        placeholderColor="text-onyx"
        placeholder={t("HINT_ENTER_EVENTS_TITLE")}
        error={errors.title?.message}
      />

      <InputField
        showLabel={true}
        name="description"
        label={t("LABEL_DESCRIPTION")}
        control={control}
        keyboardType="default"
        backgroundColor="bg-obsidian"
        placeholder={t("HINT_ENTER_EVENT_DESCRIPTION")}
        multiline
        numberOfLines={3}
      />

      {/* CR-B2-10: event type toggle — schema-blocked, client UI only until Data Gateway sync */}
      <View className="mt-1 mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_EVENT_TYPE")}
        </Text>
        <Controller
          control={control}
          name="eventType"
          render={({ field }) => (
            <View className="flex-row rounded-xl overflow-hidden border border-ash-3">
              {[EventType.IN_PERSON, EventType.ONLINE].map((type) => (
                <TouchableOpacity
                  key={type}
                  className={`flex-1 py-2.5 items-center ${field.value === type ? "bg-primary" : "bg-chalk"}`}
                  onPress={() => field.onChange(type)}
                  activeOpacity={0.8}
                >
                  <Text className={`text-sm font-medium ${field.value === type ? "text-white" : "text-onyx"}`}>
                    {t(type === EventType.IN_PERSON ? "LABEL_IN_PERSON_EVENT" : "LABEL_ONLINE_EVENT")}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        />
      </View>

      <View className="mt-1 mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_SINGLE_DAY_EVENT")}
        </Text>
        <View className="flex-row items-center justify-between rounded-xl px-4 py-3 md:border md:border-ash-3">
          <Text className="text-sm text-ash-2 flex-1 pr-4">
            {t("HINT_SINGLE_DAY_EVENT")}
          </Text>
          <Controller
            control={control}
            name="singleDay"
            render={({ field }) => (
              <ScToggle value={field.value} onChange={field.onChange} />
            )}
          />
        </View>
      </View>

      <View className="mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_DATE_AND_TIME")}
        </Text>
        <Controller
          control={control}
          name="startDate"
          rules={{
            required: {
              value: true,
              message: t("ERROR_DATE_AND_TIME_REQUIRED"),
            },
          }}
          render={() => (
            <Pressable
              onPress={() => setDatePickerOpen(true)}
              className={`flex-row items-center gap-3 bg-chalk rounded-xl px-4 py-3 border hover:bg-ash-3 ${
                errors.startDate ? "border-red-600" : "border-ash-3"
              }`}
            >
              <View className="flex-1">
                {startDate ? (
                  <>
                    <View className="flex-row items-center gap-2 flex-wrap">
                      <Text className="text-sm font-semibold text-onyx">
                        {formatDisplayDate(startDate)}
                      </Text>
                      {!singleDay && endDate && (
                        <>
                          <Text className="text-sm text-ash-2">→</Text>
                          <Text className="text-sm font-semibold text-onyx">
                            {formatDisplayDate(endDate)}
                          </Text>
                        </>
                      )}
                    </View>
                    {startTime && endTime && (
                      <View className="flex-row items-center gap-2 mt-0.5">
                        <Text className="text-xs text-ash-2">
                          {formatDisplayTime(startTime)}
                        </Text>
                        <Text className="text-xs text-ash-2">→</Text>
                        <Text className="text-xs text-ash-2">
                          {formatDisplayTime(endTime)}
                        </Text>
                      </View>
                    )}
                  </>
                ) : (
                  <Text className="text-sm text-gray-dark">
                    {t("HINT_SELECT_DATE_AND_TIME")}
                  </Text>
                )}
              </View>
              <CalendarEdit
                size={20}
                color={themeVars["--color-primary-default"] as string}
              />
              {startDate && (
                <Text className="text-xs text-primary font-medium">
                  {t("LABEL_EDIT")}
                </Text>
              )}
            </Pressable>
          )}
        />
        {errors.startDate && (
          <Text className="text-red-600 text-xs mt-1">
            {errors.startDate.message as string}
          </Text>
        )}
      </View>

      <View className="mb-4">
        <Controller
          control={control}
          name="categoryIds"
          rules={{
            validate: (val) =>
              (val && val.length > 0) || t("ERROR_CATEGORY_REQUIRED"),
          }}
          render={({ field }) => {
            return (
              <>
                <CategorySelector
                  entityType="events"
                  selectedIds={field.value ?? []}
                  onSelectedIdsChange={field.onChange}
                />
                {errors.categoryIds && (
                  <Text className="text-red-600 text-xs mt-1">
                    {errors.categoryIds.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />
      </View>

      {/* CR-B2-10: location for in-person; meeting link for online.
          The interactive map picker was removed: reverse-geocoded pin names
          rarely matched the venue people actually meant ("Chang Lam" for an
          office two doors down), and there was no way to correct them, so the
          venue is now typed in full — building, floor, landmark and all. */}
      {!isOnlineEvent ? (
        <View className="mb-4">
          <InputField
            showLabel={true}
            name="locationName"
            label={t("LABEL_LOCATION")}
            control={control}
            rules={{
              required: !isOnlineEvent
                ? { value: true, message: t("ERROR_LOCATION_REQUIRED") }
                : false,
            }}
            keyboardType="default"
            backgroundColor="bg-chalk"
            // `placeholderColor` is handed to RN's `placeholderTextColor`, which
            // takes a colour value, not a class — so the "text-onyx" the other
            // fields pass is not a dark placeholder, it is an invalid one. The
            // example venue must read as a hint rather than as typed input, so
            // it gets the muted token explicitly.
            placeholderColor={themeVars["--color-ash-1"] as string}
            placeholder={t("HINT_ENTER_LOCATION")}
            error={errors.locationName?.message}
          />
          <Text className="text-xs text-ash-2 mt-1">
            {t("HINT_LOCATION_MANUAL")}
          </Text>
        </View>
      ) : (
        <View className="mb-4">
          <InputField
            showLabel={true}
            name="meetingLink"
            label={t("LABEL_MEETING_LINK")}
            control={control}
            rules={{
              required: isOnlineEvent ? { value: true, message: t("ERROR_MEETING_LINK_REQUIRED") } : false,
              pattern: isOnlineEvent
                ? { value: /^https?:\/\/.+/, message: t("ERROR_MEETING_LINK_INVALID") }
                : undefined,
            }}
            // This wrapper's keyboardType union does not include RN's "url";
            // "default" is the closest accepted value.
            keyboardType="default"
            backgroundColor="bg-chalk"
            placeholderColor="text-onyx"
            placeholder={t("HINT_ENTER_MEETING_LINK")}
            error={errors.meetingLink?.message}
          />
        </View>
      )}

      <View className="mb-6">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_COVER_IMAGE")}
        </Text>
        <TouchableOpacity
          onPress={handlePickImage}
          className={`${coverImage || existingCoverUrl ? "py-2" : "py-6"} border-2 border-dashed border-ash-3 rounded-lg p-3 items-center justify-center bg-chalk hover:bg-ash-3`}
        >
          {coverImage || existingCoverUrl ? (
            <>
              <RNImage
                source={{
                  uri: coverImage ? coverImage.uri : existingCoverUrl!,
                }}
                style={{ width: "100%", height: 160, borderRadius: 8 }}
                resizeMode="contain"
              />
              <TouchableOpacity
                onPress={(e) => {
                  e.stopPropagation();
                  setValue("coverImage", null);
                  if (!coverImage) onRemoveExistingCover?.();
                }}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                className="rounded-full absolute top-4 right-4 hover:bg-ash-3"
              >
                <CloseCircle
                  size={24}
                  color={themeVars["--color-ash-1"] as string}
                  variant="Bold"
                />
              </TouchableOpacity>
            </>
          ) : (
            <View className="items-center">
              <View className="w-12 h-12 bg-gray-light rounded-full items-center justify-center mb-2">
                <Image size={32} color={themeVars["--color-primary-default"] as string} />
              </View>
              <Text className="text-primary font-medium text-sm mb-1">
                {t("LABEL_UPLOAD_IMAGE")}
              </Text>
              <Text className="text-gray-light-2 text-xs">
                {t("LABEL_JPG_PNG_UPTO_20MB")}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      <View className="mb-6">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_ATTACHMENTS")}
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View className="flex-row gap-2 mb-2">
            {combinedAttachments.map((item) => (
              <FileAttachment
                key={item.type === "existing" ? item.url : item.file.uri}
                file={{
                  url: item.type === "existing" ? item.url : item.file.uri,
                  file_name:
                    item.type === "existing" ? undefined : item.file.name,
                  file_size:
                    item.type === "existing" ? null : (item.file.size ?? 0),
                }}
                onRemove={() => {
                  if (item.type === "existing") {
                    handleRemoveExistingAttachment(item.url);
                  } else {
                    handleRemoveNewAttachment(
                      attachments.findIndex(
                        (file) => file.uri === item.file.uri,
                      ),
                    );
                  }
                }}
              />
            ))}
          </View>
        </ScrollView>

        <TouchableOpacity
          onPress={handlePickAttachments}
          className="flex-row items-center border-2 border-dashed border-ash-3 rounded-lg p-3 gap-2 self-start hover:bg-ash-3"
          activeOpacity={0.7}
        >
          <AttachCircle size={20} color={themeVars["--color-ash-1"] as string} />
          <Text className="text-ash-2 font-medium text-sm">
            {t("LABEL_ADD_ATTACHMENTS")}
          </Text>
        </TouchableOpacity>
      </View>

      <View className="flex-row justify-between">
        <ScButton text={t("BTN_CANCEL")} mode="outlined" onPress={onCancel} />
        <View className="flex-row gap-4">
          <ScButton text={t("BTN_PREVIOUS")} mode="outlined" disabled />
          <ScButton
            text={t("BTN_CONTINUE")}
            onPress={handleSubmit(handleSubmitWithExisting)}
          />
        </View>
      </View>

      <DateTimePickerModal
        visible={datePickerOpen}
        variant="date-time"
        singleDay={singleDay}
        initialValues={{
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          startTime: toHHmm(startTime) || undefined,
          endTime: toHHmm(endTime) || undefined,
        }}
        onClose={() => setDatePickerOpen(false)}
        onApply={(result) => {
          setValue("startDate", result.startDate ?? "", {
            shouldValidate: true,
          });
          setValue("endDate", result.endDate ?? "");
          setValue("startTime", result.startTime);
          setValue("endTime", result.endTime);
          setDatePickerOpen(false);
        }}
      />
    </View>
  );
};

export default Step1Details;
